import React, {useCallback, useEffect, useState} from 'react';
import {api} from './Api.js'
import {useNavigate} from 'react-router-dom'
import PreflightLoader, {parseISODuration} from "./PreflightLoader";
import {config} from "./Constants";
import styles from './css/Practice.module.css';

const hasActiveSpeakingSession = (role) => Boolean(role.activeSessionId || role.activeSession);

const getActiveSessionUpdatedAt = (role) => (
    role.activeSessionUpdatedAt || role.activeSession?.updatedAt || null
);

const sortRoles = (roles, type) => {
    if (type !== 'SPEAKING') {
        return roles;
    }

    return [...roles].sort((left, right) => {
        const leftActive = hasActiveSpeakingSession(left);
        const rightActive = hasActiveSpeakingSession(right);
        if (leftActive !== rightActive) {
            return rightActive ? 1 : -1;
        }

        if (!leftActive) {
            return 0;
        }

        return new Date(getActiveSessionUpdatedAt(right) || 0) - new Date(getActiveSessionUpdatedAt(left) || 0);
    });
};

const WritingRoles = ({ showNotification, type, setRole }) => {
    const [roles, setRoles] = useState(null);
    const [waiting, setWaiting] = useState(false);
    const [expectedWaitTime, setExpectedWaitTime] = useState(0);

    const navigate = useNavigate();
    const backPath = type === 'SPEAKING' ? '/' : '/writing';
    const backLabel = type === 'SPEAKING' ? 'Back to home' : 'Back';
    const activity = type === 'SPEAKING'
        ? {eyebrow: 'Speaking practice', title: 'Choose a speaking scenario', description: 'Pick a situation and speak naturally. You can continue an unfinished conversation.', icon: 'mic'}
        : type === 'TRANSLATING'
            ? {eyebrow: 'Translation practice', title: 'Choose a translation set', description: 'Work through short phrases and get useful feedback after every answer.', icon: 'translate'}
            : {eyebrow: 'Free conversation', title: 'Choose a conversation', description: 'Pick a scenario to write in English with a clear purpose and helpful tools.', icon: 'forum'};


    const fetchRoles = useCallback(async () => {
        setWaiting(true);
        if (config.enablePreflight) {
            const waitRes = await api.fetchWaitTime('/writing/role/list', navigate, showNotification, {type: type});
            if (api.isValidResponse(waitRes)) {
                setExpectedWaitTime(parseISODuration(waitRes.data.expectedWaitTime));
            }
        }
        const response = await api.fetchWritingRoles(type, navigate, showNotification);
        if (api.isValidResponse(response)) {
            setWaiting(false);
            setRoles(sortRoles(response.data, type));
            setExpectedWaitTime(0);
        } else {
            setWaiting(false);
            setExpectedWaitTime(0);
        }
    }, [navigate, showNotification, type]);

    useEffect(() => {
        if (roles === null) {
            setWaiting(true);
            fetchRoles();
        }
    }, [roles, fetchRoles]);

    const preloader = () => {
        return (
            <div className="common-preloader-form">
                <PreflightLoader expectedWaitTime={expectedWaitTime}/>
            </div>
        );
    }

    const renderForm = () => {
        return (
            <main className={styles.page}>
                <header className={styles.pageIntro}>
                    <div className={styles.eyebrow}><i className="material-icons">{activity.icon}</i> {activity.eyebrow}</div>
                    <h1>{activity.title}</h1>
                    <p>{activity.description}</p>
                </header>
                <section className={styles.roleList} aria-label="Available scenarios">
                    {roles.map(r => (
                        <button className={styles.roleCard} key={r.roleId} onClick={() => setRole(r)} type="button">
                            <span className={styles.roleAvatar}><i className="material-icons">person</i></span>
                            <span>
                                <span className={styles.roleTitle}>{r.summary}</span>
                                <span className={styles.roleDescription}>{r.description}</span>
                                <span className={styles.roleMeta}>
                                    {r.percent !== null && <span className={`chip ${styles.chip} ${styles.progressChip}`}>{r.percent}% progress</span>}
                                    {type === "WRITING" && r.userGroupId !== null && <span className={`chip ${styles.chip} ${styles.activeChip}`}>In progress</span>}
                                    {type === "SPEAKING" && hasActiveSpeakingSession(r) && <span className={`chip ${styles.chip} ${styles.speakingChip}`}>Continue speaking</span>}
                                </span>
                            </span>
                            <i className={`material-icons ${styles.roleArrow}`}>chevron_right</i>
                        </button>
                    ))}
                </section>
                <div className="center-align">
                    <button className={styles.backLink} onClick={() => navigate(backPath)} type="button">
                        <i className="material-icons">arrow_back</i> {backLabel}
                    </button>
                </div>
            </main>
        )
    };

    if (waiting || roles === null) {
        return preloader();
    }

    return (
        <div className="common-container" style={{width: '100%', maxWidth: '100%', boxShadow: 'none', background: 'transparent', padding: 0}}>
            {renderForm()}
        </div>
    );
};

export default WritingRoles;
